import { format } from 'date-fns'
import { saveAs } from 'file-saver'
import { BookType, utils, writeFile } from 'xlsx'

import { InternalOrganization, InternalUser } from '../../types/Organizations'

export enum UserStatus {
  Active = 'active',
  Inactive = 'inactive',
  InvitePending = 'invite-pending',
}

export const userStatusLabelKeys: Record<UserStatus, string> = {
  [UserStatus.Active]: 'internal-csm:organization_users_status_active',
  [UserStatus.Inactive]: 'internal-csm:organization_users_status_inactive',
  [UserStatus.InvitePending]: 'internal-csm:organization_users_status_invite_pending',
}

/** An active user who has never logged in still has the invitation pending. */
export const getUserStatus = (user: Pick<InternalUser, 'active' | 'lastLoginAt'>) => {
  if (!user.active) return UserStatus.Inactive
  return user.lastLoginAt > 0 ? UserStatus.Active : UserStatus.InvitePending
}

/** "First Last" for confirmations, or the e-mail when the user has no name. */
export const getUserDisplayName = (user: Pick<InternalUser, 'firstName' | 'lastName' | 'email'>) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email

/** The unique roles of the organization's packages, sorted like legacy's "User Roles". */
export const getOrganizationRoles = (organization?: Pick<InternalOrganization, 'packages'>) =>
  Array.from(new Set((organization?.packages ?? []).flatMap(({ roles }) => roles ?? []))).sort()

const exportStatusLabels: Record<UserStatus, string> = {
  [UserStatus.Active]: 'Active',
  [UserStatus.Inactive]: 'Inactive',
  [UserStatus.InvitePending]: 'Active / invite pending',
}

export type UserExportRow = {
  Email: string
  'First name': string | null
  'Last name': string | null
  'Last login': string
  Status: string
}

/** Legacy's export rows in API order; the headers and values are English like legacy's. */
export const toUserExportRows = (users: InternalUser[]): UserExportRow[] =>
  users.map((user) => ({
    Email: user.email,
    'First name': user.firstName,
    'Last name': user.lastName,
    'Last login': user.lastLoginAt > 0 ? format(user.lastLoginAt, 'dd.MM.yyyy') : 'N/A',
    Status: exportStatusLabels[getUserStatus(user)],
  }))

/** Ends with a line break like legacy's file, which an older SheetJS wrote. */
export const toUsersCsv = (rows: UserExportRow[]) => `${utils.sheet_to_csv(utils.json_to_sheet(rows), { FS: ';' })}\n`

export const getUsersExportFileName = (organizationName: string) => `list-users-${organizationName}`

/** Legacy's CSV is `;`-separated without a byte order mark; its XLSX sheet is "Data". */
export const exportOrganizationUsers = (bookType: BookType, users: InternalUser[], organizationName: string) => {
  const rows = toUserExportRows(users)
  const fileName = `${getUsersExportFileName(organizationName)}.${bookType}`
  if (bookType === 'csv') {
    saveAs(new Blob([toUsersCsv(rows)], { type: 'text/csv;charset=utf-8;' }), fileName)
    return
  }
  const workbook = utils.book_new()
  utils.book_append_sheet(workbook, utils.json_to_sheet(rows), 'Data')
  writeFile(workbook, fileName)
}
